import * as React from "react";
import { useState, useMemo } from "react";
import styled from "styled-components";
import { OrderedMap } from "immutable";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import BootstrapModalConfirm from "components/bootstrap/BootstrapModalConfirm";
import { Alert, Button } from "components/bootstrap";
import { Icon } from "components/common";
import { DataWell } from "common/components";
import { useRemoveEvidence } from "security-app/hooks/useInvestigationsAPI";
import { fetchPaginatedInvestigationSecurityEvents } from "security-app/hooks/api/investigationsAPI";
import PaginatedEntityTable from "components/common/PaginatedEntityTable";
import { deserializeSecurityEventEntity } from "security-app/hooks/api/securityEventsAPI";
import {
  attributes as securityEventsAttributes,
  useSecurityEventsTableElements
} from "security-app/components/SecurityEvents/Alerts/Constants";
import columnRenderers from "security-app/components/SecurityEvents/Alerts/ColumnRenderers";
import { TagsRenderer } from "components/events/events/ColumnRenderers";
import ExpandedEventDetails from "security-app/components/SecurityEvents/Alerts/ExpandedEventDetails";
import FilterValueRenderers from "security-app/components/SecurityEvents/Alerts/FilterValueRenderers";
import EvidenceSectionHeader from "security-app/components/Investigations/Details/OverviewSegment/EvidenceSectionHeader";
import { investigationEventsQueryKey } from "security-app/hooks/queryKeys";
import Routes from "routing/Routes";
import useGoToEvidencePage from "./useGoToEvidencePage";
import { IconButton } from "../styled.components";
const TableContainer = styled.div`
  overflow: auto;
`;
const EmptyEventAlert = styled(Alert)`
  margin: 0;
  width: 100%;
`;
const eventRowOverride = (event) => {
  if (!event.timestamp) {
    return /* @__PURE__ */ React.createElement(EmptyEventAlert, { compact: true }, "This event with ID ", event.id, " is no longer available. It may have expired from the event index or is no longer accessible with your current permissions.");
  }
  return void 0;
};
function Events({ investigation, isExpanded, onToggleExpanded }) {
  var _a;
  const { permissions } = useCurrentUser();
  const { defaultLayout: securityEventsDefaultLayout } = useSecurityEventsTableElements();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const goToEvents = useGoToEvidencePage(
    investigation.id,
    Routes.ALERTS.LIST,
    OrderedMap({ associated_investigations: [investigation.id] })
  );
  const canManageEvidence = useMemo(
    () => !investigation.archived && isPermitted(permissions, "investigations:edit"),
    [investigation.archived, permissions]
  );
  const { removeEvidence, removingEvidence } = useRemoveEvidence();
  const nonSortableSecurityEventsAttributes = ((_a = securityEventsAttributes) != null ? _a : []).map((attribute) => ({
    ...attribute,
    sortable: false
  }));
  const tableLayout = useMemo(
    () => ({
      ...securityEventsDefaultLayout,
      entityTableId: "investigation-details-security-events",
      defaultPageSize: 10,
      defaultSort: void 0
    }),
    [securityEventsDefaultLayout]
  );
  const investigationColumnRenderers = useMemo(
    () => ({
      ...columnRenderers,
      attributes: {
        ...columnRenderers.attributes,
        tags: {
          ...columnRenderers.attributes.tags,
          renderCell: (tags) => /* @__PURE__ */ React.createElement(TagsRenderer, { tags, interactive: false })
        }
      }
    }),
    []
  );
  const fetchInvestigationEvents = async (searchParams) => {
    var _a2, _b, _c;
    const currentPage = (_a2 = searchParams.page) != null ? _a2 : 1;
    const pageSize = searchParams.pageSize || tableLayout.defaultPageSize;
    const result = await fetchPaginatedInvestigationSecurityEvents(investigation.id, currentPage, pageSize);
    const list = ((_b = result.elements) != null ? _b : []).map(deserializeSecurityEventEntity);
    const total = (_c = result.pagination) == null ? void 0 : _c.total;
    return {
      attributes: nonSortableSecurityEventsAttributes,
      list,
      pagination: { total }
    };
  };
  const investigationEventsEntitiesKeyFn = (searchParams) => investigationEventsQueryKey(investigation.id, investigation.updated_at, searchParams);
  const removeEvent = async () => {
    await removeEvidence({ invId: investigation.id, payload: { events: [deleteEventId] } });
    setShowDeleteDialog(false);
    setDeleteEventId(null);
  };
  const onDelete = (eventId) => (e) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
    setDeleteEventId(eventId);
  };
  const onCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteEventId(null);
  };
  const renderEventActions = (event) => {
    var _a2;
    return /* @__PURE__ */ React.createElement(IconButton, { $danger: true, "aria-label": `Delete event ${(_a2 = event.message) != null ? _a2 : event.event_id}`, onClick: onDelete(event.id) }, /* @__PURE__ */ React.createElement(Icon, { name: "close" }));
  };
  const renderExpandedEventDetails = (securityEvent) => /* @__PURE__ */ React.createElement(ExpandedEventDetails, { securityEvent, defaultLayout: tableLayout, interactiveTags: false });
  const expandedSections = {
    restFieldsExpandedSection: {
      title: "",
      content: renderExpandedEventDetails,
      disableHeader: true
    }
  };
  return /* @__PURE__ */ React.createElement(DataWell, { $color: "content" }, /* @__PURE__ */ React.createElement(
    EvidenceSectionHeader,
    {
      sectionName: "events",
      title: "Events",
      count: investigation.events_count,
      isExpanded,
      onToggleExpanded,
      action: canManageEvidence ? /* @__PURE__ */ React.createElement(Button, { onClick: goToEvents }, "Go to Events") : null
    }
  ), isExpanded && /* @__PURE__ */ React.createElement(TableContainer, { style: { marginTop: "0.5rem" } }, /* @__PURE__ */ React.createElement(
    PaginatedEntityTable,
    {
      humanName: "security events",
      tableLayout,
      fetchEntities: fetchInvestigationEvents,
      keyFn: investigationEventsEntitiesKeyFn,
      expandedSectionRenderers: expandedSections,
      rowOverride: eventRowOverride,
      entityAttributesAreCamelCase: false,
      columnRenderers: investigationColumnRenderers,
      filterValueRenderers: FilterValueRenderers,
      entityActions: canManageEvidence ? renderEventActions : void 0,
      externalSearch: { query: "" },
      withoutURLParams: true
    }
  )), /* @__PURE__ */ React.createElement(
    BootstrapModalConfirm,
    {
      showModal: showDeleteDialog,
      title: "Delete Event",
      onConfirm: removeEvent,
      onCancel: onCancelDelete,
      confirmButtonDisabled: removingEvidence
    },
    "Are you sure you want to delete this Event?"
  ));
}
export default Events;
